import { Badge, Button, Field, Input } from '@cloudflare/kumo';
import { useCallback, useEffect, useState } from 'react';
import { DangerButton } from '../../components/ui/DangerButton';
import { ICONS } from '../../components/ui/icons';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { useAppToast } from '../../hooks/useAppToast';

interface Version { version: number; note: string | null; createdAt: string }
interface KvResult { synced: boolean; message?: string }
interface LiveTurn { role: 'user' | 'assistant'; text: string }
interface TestRun { passed: number; failed: number; results: Array<{ id: string; name: string; pass: boolean; failures: string[] }> }

const when = (iso: string) => new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });

/**
 * Publicação do agente: publicar congela a configuração salva numa versão que
 * o runtime na Cloudflare usa; editar e salvar não muda o publicado. Rollback
 * volta para uma versão anterior.
 */
export function AgentPublishPanel({ agentId, canEdit, onChange }: { agentId: string; canEdit: boolean; onChange?: () => void }) {
  const fetchApi = useAuthenticatedFetch();
  const toast = useAppToast();
  const [versions, setVersions] = useState<Version[]>([]);
  const [published, setPublished] = useState<number | null>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  // Publicação barrada por casos de teste que falharam
  const [blocked, setBlocked] = useState<TestRun | null>(null);
  // Conversa com a versão no ar (runtime na Cloudflare), separada do teste do rascunho
  const [live, setLive] = useState<LiveTurn[]>([]);
  const [liveInput, setLiveInput] = useState('');
  const [liveSession, setLiveSession] = useState<string | null>(null);
  const [liveBusy, setLiveBusy] = useState(false);

  const load = useCallback(async () => {
    const r = await fetchApi(`/api/ai/agents/${agentId}/versions`);
    if (!r.ok) return;
    const j = await r.json();
    setVersions(j.versions);
    setPublished(j.publishedVersion);
  }, [agentId, fetchApi]);
  useEffect(() => { load(); }, [load]);

  const report = (kv: KvResult, ok: string) => {
    if (kv.synced) toast.success(ok);
    else toast.error(`${ok}, mas o runtime não foi atualizado: ${kv.message ?? 'erro desconhecido'}`);
  };

  const publish = async (force = false) => {
    setBusy(true);
    const r = await fetchApi(`/api/ai/agents/${agentId}/publish`, { method: 'POST', body: JSON.stringify({ note, force }) });
    const j = await r.json().catch(() => ({}));
    setBusy(false);
    if (r.status === 409 && j.tests) { setBlocked(j.tests); return; }
    if (!r.ok) { toast.error(j.message || 'Não foi possível publicar'); return; }
    report(j.kv, `Versão ${j.version} publicada${j.tests ? ` (${j.tests.passed} de ${j.tests.passed + j.tests.failed} casos de teste passaram)` : ''}`);
    setBlocked(null);
    setNote('');
    load(); onChange?.();
  };

  const rollback = async (version: number) => {
    setBusy(true);
    const r = await fetchApi(`/api/ai/agents/${agentId}/versions/${version}/rollback`, { method: 'POST' });
    const j = await r.json().catch(() => ({}));
    setBusy(false);
    if (!r.ok) { toast.error(j.message || 'Não foi possível voltar a versão'); return; }
    report(j.kv, `Versão ${version} voltou a ser a publicada`);
    load(); onChange?.();
  };

  const sendLive = async () => {
    const text = liveInput.trim();
    if (!text) return;
    setLive((l) => [...l, { role: 'user', text }]); setLiveInput(''); setLiveBusy(true);
    const r = await fetchApi(`/api/ai/agents/${agentId}/published/chat`, { method: 'POST', body: JSON.stringify({ text, sessionId: liveSession }) });
    const j = await r.json().catch(() => ({}));
    setLiveBusy(false);
    if (!r.ok) { toast.error(j.message || 'A versão no ar não respondeu'); return; }
    setLiveSession(j.sessionId);
    setLive((l) => [...l, { role: 'assistant', text: j.reply || (j.mode === 'handoff' ? '(conversa com a equipe)' : '(sem resposta)') }]);
  };

  return (
    <section className="brand-card space-y-3" data-testid="ai-agent-publish">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-lg font-semibold" style={{ fontSize: 16 }}>Publicação</h3>
        {published ? <Badge variant="green">No ar: versão {published}</Badge> : <Badge variant="neutral">Rascunho</Badge>}
      </div>
      <div className="text-xs !text-[var(--text-muted)]">
        Publicar congela a configuração salva numa versão. Editar e salvar não muda o que está no ar até publicar de novo.
      </div>
      {canEdit && (
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <Field label="Nota da versão (opcional)">
              <Input onChange={(e) => setNote(e.target.value)} placeholder="Ex.: novo horário de atendimento" value={note} />
            </Field>
          </div>
          <Button variant="primary" icon={ICONS.Upload} loading={busy} onClick={() => publish()} data-testid="ai-agent-publish-btn">Publicar</Button>
        </div>
      )}
      {blocked && (
        <div className="p-3 space-y-1" style={{ border: '1px solid var(--crm-border-error)', background: 'var(--crm-bg-error)', borderRadius: 8 }} data-testid="ai-agent-publish-blocked">
          <div className="font-semibold">{blocked.failed} caso{blocked.failed === 1 ? '' : 's'} de teste falhou; a versão não foi publicada.</div>
          {blocked.results.filter((t) => !t.pass).map((t) => (
            <div className="text-xs" key={t.id}>✗ {t.name}: {t.failures.join('; ')}</div>
          ))}
          <div className="flex gap-2 pt-1">
            <Button variant="secondary" onClick={() => setBlocked(null)}>Corrigir antes</Button>
            <DangerButton loading={busy} onClick={() => publish(true)} data-testid="ai-agent-publish-force">Publicar mesmo assim</DangerButton>
          </div>
        </div>
      )}
      {versions.length > 0 && (
        <ul className="space-y-2" data-testid="ai-agent-versions">
          {versions.map((v) => (
            <li key={v.version} className="flex items-center justify-between gap-2 text-sm">
              <div>
                <div className="font-semibold">Versão {v.version}{v.version === published ? ' · no ar' : ''}</div>
                <div className="text-xs !text-[var(--text-muted)]">{when(v.createdAt)}{v.note ? ` · ${v.note}` : ''}</div>
              </div>
              {canEdit && v.version !== published && (
                <Button variant="secondary" disabled={busy} onClick={() => rollback(v.version)}>Voltar para esta</Button>
              )}
            </li>
          ))}
        </ul>
      )}
      {published && canEdit && (
        <div className="space-y-2 pt-2" data-testid="ai-agent-live-test">
          <div className="font-semibold">Testar a versão no ar</div>
          <div className="text-xs !text-[var(--text-muted)]">Conversa com o agente publicado, como os clientes vão ver (inclui ferramentas de verdade).</div>
          {live.map((t, i) => (
            <div key={i} className={t.role === 'user' ? 'text-right' : ''}>
              <span style={{ display: 'inline-block', maxWidth: '85%', padding: '6px 10px', borderRadius: 10, whiteSpace: 'pre-wrap', textAlign: 'left',
                background: t.role === 'user' ? 'var(--tint-green)' : 'var(--surface-2)', border: '1px solid var(--border)' }}>{t.text}</span>
            </div>
          ))}
          {liveBusy && <div className="text-sm !text-[var(--text-muted)]">digitando…</div>}
          <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); sendLive(); }}>
            <div className="flex-1">
              <Input onChange={(e) => setLiveInput(e.target.value)} aria-label="Mensagem para a versão no ar" placeholder="Oi, tudo bem?" value={liveInput} />
            </div>
            <Button type="submit" variant="secondary" disabled={!liveInput.trim() || liveBusy}>Enviar</Button>
            {live.length > 0 && <Button variant="secondary" type="button" onClick={() => { setLive([]); setLiveSession(null); }}>Nova conversa</Button>}
          </form>
        </div>
      )}
    </section>
  );
}
